<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import type { LumiModuleKey } from '../modules';
import { sidebarCollapse, toggleSidebarCollapsed } from './sidebarCollapse';
import { rememberShellLayout } from './shellLayoutMemory';
import { TABS_LABELS_KEY } from './tabsLabels';
import { lumiDockLauncher, toggleLumiDock } from './lumiDockLauncher';
import LumiIcon from '../icons/LumiIcon.vue';

// `module` marks the shell via data-module (a hook for per-module styling and
// tests); the palette itself no longer varies by module (spec §2).
const props = withDefaults(
  defineProps<{
    module?: LumiModuleKey;
    padded?: boolean;
    menuLabel?: string;
    /** The seam handle's accessible name while the sidebar is open / folded
     * (#540). Neutral English defaults like Sidebar's history labels: every
     * app passes its own translation. */
    collapseSidebarLabel?: string;
    expandSidebarLabel?: string;
    /** The accessible names of every tab strip's overflow arrows below this
     * shell (issue 661), handed down through provide/inject so a page does not
     * pass them to each Tabs. Neutral English defaults like the labels above. */
    tabsPreviousLabel?: string;
    tabsNextLabel?: string;
  }>(),
  {
    padded: true,
    menuLabel: 'Menü',
    collapseSidebarLabel: 'Collapse sidebar',
    expandSidebarLabel: 'Expand sidebar',
    tabsPreviousLabel: 'Previous tabs',
    tabsNextLabel: 'Next tabs',
  },
);

provide(
  TABS_LABELS_KEY,
  computed(() => ({ previous: props.tabsPreviousLabel, next: props.tabsNextLabel })),
);

// Mobile-only off-canvas state. On desktop the sidebar is in-flow and this is
// inert (the hamburger and backdrop are display:none above the breakpoint).
const mobileOpen = ref(false);

function closeMobile() {
  mobileOpen.value = false;
}

// A tap on a nav entry closes the drawer, the page already open included: the
// route watch every host keeps for this does not fire when the route stays the
// same, and the drawer then stayed over the page it was asked to show. A locked
// entry opens its hint beside the entry instead, so the drawer stays for it.
function onSidebarClick(e: MouseEvent) {
  if (!mobileOpen.value || !(e.target instanceof Element)) return;
  const entry = e.target.closest('a[href], .lumi-sidebar__item');
  if (entry && !entry.classList.contains('lumi-sidebar__item--locked')) closeMobile();
}

// The open drawer follows a finger that pushes it back to the left, and goes
// when let go far or fast enough — the way a phone's own side menus close.
// Before, a swipe moved nothing and the drawer stood under the finger as if it
// had hung. The first few pixels decide the axis: a vertical drag stays the nav
// list's own scroll (the drawer is `touch-action: pan-y`), a horizontal one is
// the drawer's. On the backdrop the same drag works too.
const DRAWER_AXIS_SLOP_PX = 8;
const DRAWER_CLOSE_PX = 72;
const DRAWER_FLICK_PX = 24;
const DRAWER_FLICK_MS = 250;
const drawerRef = ref<HTMLElement | null>(null);
const dragX = ref(0);
let drag: { x: number; y: number; t: number; width: number; axis: 'x' | 'y' | null } | null = null;

function onDrawerTouchStart(e: TouchEvent) {
  if (!mobileOpen.value || e.touches.length !== 1) return;
  const p = e.touches[0];
  drag = { x: p.clientX, y: p.clientY, t: e.timeStamp, width: drawerRef.value?.offsetWidth || 1, axis: null };
}
function onDrawerTouchMove(e: TouchEvent) {
  if (!drag || e.touches.length !== 1) return;
  const p = e.touches[0];
  const dx = p.clientX - drag.x;
  const dy = p.clientY - drag.y;
  if (!drag.axis) {
    if (Math.abs(dx) < DRAWER_AXIS_SLOP_PX && Math.abs(dy) < DRAWER_AXIS_SLOP_PX) return;
    drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
  }
  if (drag.axis === 'x') dragX.value = Math.max(-drag.width, Math.min(0, dx));
}
function onDrawerTouchEnd(e: TouchEvent) {
  if (!drag) return;
  const flick = drag.axis === 'x' && dragX.value <= -DRAWER_FLICK_PX && e.timeStamp - drag.t < DRAWER_FLICK_MS;
  if (dragX.value <= -DRAWER_CLOSE_PX || flick) closeMobile();
  dragX.value = 0;
  drag = null;
}
// While dragged the drawer sits under the finger with no easing; let go, it
// eases to wherever it ends (open again, or shut). The backdrop thins with it.
const drawerStyle = computed(() => (dragX.value ? { transform: `translateX(${dragX.value}px)`, transition: 'none' } : undefined));
const backdropStyle = computed(() =>
  dragX.value && drag ? { opacity: String(1 + dragX.value / drag.width), transition: 'none' } : undefined,
);

// `[` folds and unfolds the sidebar from anywhere except where it is typed
// (#540). Ctrl/Meta combinations belong to the browser; Ctrl+Alt is AltGr,
// which is how `[` is typed on a German keyboard, so that one passes. The
// mobile drawer has no rail, so the key is inert below the breakpoint.
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    mobileOpen.value = false;
    return;
  }
  if (e.key !== '[' || e.defaultPrevented || e.repeat || e.metaKey) return;
  if (e.ctrlKey && !e.altKey) return;
  if (isTypingTarget(e.target)) return;
  if (typeof window.matchMedia === 'function' && !window.matchMedia('(min-width: 768px)').matches) return;
  toggleSidebarCollapsed();
}

// Exposed so a consumer can close the mobile drawer programmatically (e.g. on
// route change), in addition to the `close` slot prop handed to #sidebar.
defineExpose({ close: closeMobile });

// The boot frame of the next page load draws the strip above the shell and the
// docked Lumi panel at the size they have as this page leaves (issue 533,
// shellLayoutMemory.ts) — both appear only after the app has read its data.
const topRef = ref<HTMLElement | null>(null);
const dockRef = ref<HTMLElement | null>(null);

function onPageHide() {
  rememberShellLayout({ top: topRef.value?.offsetHeight ?? 0, dock: dockRef.value?.offsetWidth ?? 0 });
}

// The floating Lumi window (position:fixed, bottom-right) is 620px tall: on a
// laptop-height screen it reaches up over the right end of the banner strip — the
// very place a banner keeps its button (issue 713: the needs-you strip's Hide). The
// shell tells the window where the banner ends (`--lumi-shell-top-clear`, read by
// LumiShell's float max-height), so the window gives up height instead of covering
// the strip. Unset (no banner) the window keeps its own rule.
const rootEl = ref<HTMLElement | null>(null);
let bannerEl: HTMLElement | null = null;
let bannerObserver: ResizeObserver | null = null;
function publishBannerClearance() {
  const root = rootEl.value;
  if (!root) return;
  // An empty banner slot (the strip is v-if'd away) is 0px tall and clears nothing.
  const box = bannerEl?.getBoundingClientRect();
  if (!box || box.height <= 0) {
    root.style.removeProperty('--lumi-shell-top-clear');
    return;
  }
  root.style.setProperty('--lumi-shell-top-clear', `${Math.ceil(box.bottom)}px`);
}
// A function ref: the banner slot is v-if'd and a strip arrives after a fetch, so
// the element follows the template (slots are not reactive to watch).
function setBannerEl(el: unknown) {
  const next = (el as HTMLElement | null) ?? null;
  if (next === bannerEl) return; // Vue calls a function ref on every patch
  bannerEl = next;
  bannerObserver?.disconnect();
  if (bannerEl && bannerObserver) bannerObserver.observe(bannerEl);
  publishBannerClearance();
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    document.addEventListener('keydown', onKey);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('resize', publishBannerClearance);
  }
  if (typeof ResizeObserver !== 'undefined') {
    bannerObserver = new ResizeObserver(publishBannerClearance);
    if (bannerEl) bannerObserver.observe(bannerEl);
  }
  publishBannerClearance();
});
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('resize', publishBannerClearance);
  }
  bannerObserver?.disconnect();
});
</script>

<template>
  <div ref="rootEl" class="lumi-appshell" :data-module="module">
    <!-- Optional full-width strip ABOVE the whole shell row — sidebar, main and
         dock alike (#409 follow-ups, F2). Where a bar that frames the entire app
         goes, e.g. the demo stage bar; `#banner` below stays the strip inside the
         main column. Slot-gated so it reserves no space when unused. -->
    <div v-if="$slots.top" ref="topRef" class="lumi-appshell__top"><slot name="top" /></div>
    <div class="lumi-appshell__row">
      <div
        ref="drawerRef"
        class="lumi-appshell__sidebar"
        :class="{ 'lumi-appshell__sidebar--open': mobileOpen }"
        :style="drawerStyle"
        @click="onSidebarClick"
        @touchstart.passive="onDrawerTouchStart"
        @touchmove.passive="onDrawerTouchMove"
        @touchend="onDrawerTouchEnd"
        @touchcancel="onDrawerTouchEnd"
      >
        <slot name="sidebar" :close="closeMobile" />
        <!-- The fold handle (#540): a round knob on the seam between the sidebar
             and the panel, halfway down, 24px to see and 44px to hit. The seam is
             the only edge the open sidebar has, so the control that moves it sits
             there — not in the header (a toolbar, spec §5) and not in the foot
             (the account row, spec §4). Desktop only. -->
        <button
          type="button"
          class="lumi-appshell__fold"
          :aria-label="sidebarCollapse.collapsed ? expandSidebarLabel : collapseSidebarLabel"
          :title="`${sidebarCollapse.collapsed ? expandSidebarLabel : collapseSidebarLabel} ( [ )`"
          :aria-expanded="!sidebarCollapse.collapsed"
          @click="toggleSidebarCollapsed"
        >
          <span class="lumi-appshell__fold-knob">
            <LumiIcon
              name="fold-sidebar"
              class="lumi-appshell__fold-glyph"
              :class="{ 'lumi-appshell__fold-glyph--collapsed': sidebarCollapse.collapsed }"
              width="14"
              height="14"
              stroke-width="2.4"
            />
          </span>
        </button>
      </div>
      <!-- Always in the page below the breakpoint, so it fades in and out with the
           drawer instead of popping; closed, it is see-through and lets taps pass. -->
      <div
        class="lumi-appshell__backdrop"
        :class="{ 'lumi-appshell__backdrop--open': mobileOpen }"
        :style="backdropStyle"
        aria-hidden="true"
        @click="closeMobile"
        @touchstart.passive="onDrawerTouchStart"
        @touchmove.passive="onDrawerTouchMove"
        @touchend="onDrawerTouchEnd"
        @touchcancel="onDrawerTouchEnd"
      />
      <!-- The cream content panel on the sand canvas (spec §4): 1px panel edge,
           16px radius, soft shadow, 16px margin — 6px on the sidebar side, where
           the nav column lies open on the canvas with no surface of its own. -->
      <div class="lumi-appshell__main">
        <div class="lumi-appshell__topbar">
          <button
            type="button"
            class="lumi-appshell__menu"
            :aria-label="menuLabel"
            :aria-expanded="mobileOpen"
            @click="mobileOpen = !mobileOpen"
          >
            <LumiIcon name="menu" width="22" height="22" stroke-width="2" />
          </button>
          <div class="lumi-appshell__header"><slot name="header" /></div>
          <!-- Lumi on a phone. The account row's button (Sidebar.vue) is the way in
               everywhere else, but below the breakpoint it sits at the foot of the
               closed drawer, two taps and a scroll away from the camera Lumi takes a
               timesheet from. The app bar is where a phone app keeps the action it
               is about, so the same toggle stands here too, through the same
               lumiDockLauncher bridge: rendered while a LumiShell is mounted, marked
               while it is open. It sits in the bar, not over the page — the floating
               pill that covered form actions stays retired (LumiShell.vue). -->
          <button
            v-if="lumiDockLauncher.visible"
            type="button"
            class="lumi-appshell__lumi"
            :class="{ 'lumi-appshell__lumi--open': lumiDockLauncher.expanded }"
            :aria-expanded="lumiDockLauncher.expanded"
            data-testid="appshell-lumi"
            @click="toggleLumiDock"
          >
            <LumiIcon name="lumi" width="20" height="20" />
            <span>{{ lumiDockLauncher.label }}</span>
          </button>
        </div>
        <!-- Optional full-width banner directly below the header (e.g. the
             demo-mode banner, issue 142). Slot-gated so it reserves no space when
             unused; sits above the scrolling content so it stays pinned. -->
        <div v-if="$slots.banner" :ref="setBannerEl" class="lumi-appshell__banner"><slot name="banner" /></div>
        <main
          id="main-content"
          tabindex="-1"
          class="lumi-appshell__content"
          :class="{ 'lumi-appshell__content--padded': padded }"
        >
          <slot />
        </main>
      </div>
      <!-- Right-dock region for the shared Lumi assistant shell (issue 137). The
           docked LumiShell sits in-flow here, so dock mode reflows the content
           area; in float mode the shell is position:fixed and this region
           collapses to 0 width (reserves nothing). Intentionally dumb: the width
           is driven entirely by the slotted LumiShell, so AppShell needs no
           mode/width state of its own. Isolated for an easy shell-hygiene merge. -->
      <div v-if="$slots.dock" ref="dockRef" class="lumi-appshell__dock"><slot name="dock" /></div>
    </div>
  </div>
</template>

<style>
/* `100dvh` after the `100vh` fallback: on a phone `100vh` is the viewport with
   the browser's bars retracted, so the shell stood taller than the screen it was
   shown on and the whole page slid under the address bar. */
.lumi-appshell{position:relative;display:flex;flex-direction:column;height:100vh;height:100dvh;background:var(--shell-canvas);overflow:hidden;font-family:var(--font-body);touch-action:manipulation;-webkit-tap-highlight-color:transparent;}
/* The shell is the screen and only its regions scroll, so the document itself
   never should: a drag on the app bar, a banner or the drawer used to rubber-band
   the whole app — and pull it into the browser's pull-to-refresh, which reloaded
   the page mid-swipe. `touch-action: manipulation` above takes the double-tap
   zoom (pinch zoom stays); the tap highlight gives way to each control's own
   pressed state. */
html:has(.lumi-appshell),html:has(.lumi-appshell) body{overscroll-behavior:none;}
.lumi-appshell__top{flex-shrink:0;width:100%;}
/* The shell row proper: sidebar + main (+ dock). `min-height:0` lets the row
   shrink under the top strip instead of overflowing the viewport. */
.lumi-appshell__row{display:flex;flex:1;min-height:0;}
.lumi-appshell__sidebar{position:relative;display:flex;flex-shrink:0;height:100%;}
/* The fold handle: its 44px hit box is centred on the seam (half the 6px
   sidebar-side panel gap past the sidebar's edge), halfway down the row. */
.lumi-appshell__fold{position:absolute;top:50%;right:calc(-22px - var(--panel-gap-sidebar) / 2);z-index:var(--layer-raised);display:flex;align-items:center;justify-content:center;width:44px;height:44px;padding:0;border:none;background:none;border-radius:var(--radius-pill);cursor:pointer;transform:translateY(-50%);}
.lumi-appshell__fold-knob{display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:var(--radius-pill);background:var(--surface-card);border:1px solid var(--panel-edge);color:var(--text-secondary);box-shadow:var(--shadow-sm);transition:border-color var(--dur-fast),color var(--dur-fast);}
.lumi-appshell__fold-glyph--collapsed{transform:scaleX(-1);}
.lumi-appshell__fold:hover .lumi-appshell__fold-knob{border-color:var(--nav-highlight);color:var(--nav-highlight);}
.lumi-appshell__fold:focus-visible{outline:none;}
.lumi-appshell__fold:focus-visible .lumi-appshell__fold-knob{color:var(--text-heading);box-shadow:0 0 0 2px var(--surface-card),0 0 0 4px var(--focus-ring);}
.lumi-appshell__main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0;margin:var(--panel-gap) var(--panel-gap) var(--panel-gap) var(--panel-gap-sidebar);background:var(--shell-panel);border:1px solid var(--panel-edge);border-radius:var(--panel-radius);box-shadow:var(--shadow-panel);}
.lumi-appshell__banner{flex-shrink:0;}
.lumi-appshell__topbar{display:flex;align-items:stretch;flex-shrink:0;}
.lumi-appshell__header{flex:1;min-width:0;}
.lumi-appshell__menu{display:none;align-items:center;justify-content:center;width:var(--topbar-h);flex-shrink:0;border:none;background:none;color:var(--text-body);cursor:pointer;}
.lumi-appshell__menu:hover{color:var(--text-heading);}
.lumi-appshell__lumi{display:none;align-items:center;gap:6px;flex-shrink:0;height:36px;padding:0 var(--space-3) 0 10px;border:1px solid var(--panel-edge);border-radius:var(--radius-pill);background:var(--surface-card);color:var(--text-heading);font-family:inherit;font-size:var(--text-sm);font-weight:var(--weight-semibold);cursor:pointer;}
.lumi-appshell__lumi svg{color:var(--attention);}
.lumi-appshell__lumi--open{background:var(--btn-primary-bg);border-color:transparent;color:var(--btn-primary-fg);}
.lumi-appshell__lumi:focus-visible,.lumi-appshell__menu:focus-visible{outline:none;box-shadow:0 0 0 2px var(--focus-ring);}
/* `--lumi-content-pad` tells a sticky PageToolbar how far to bleed so its
   fade spans the whole panel width instead of stopping at the padding. */
.lumi-appshell__content{flex:1;overflow-y:auto;--lumi-content-pad:0px;}
.lumi-appshell__content--padded{padding:var(--space-6);--lumi-content-pad:var(--space-6);}
.lumi-appshell__backdrop{display:none;}
/* Right-dock region (issue 137): sizes to the slotted LumiShell; 0 width in float mode. */
.lumi-appshell__dock{flex-shrink:0;height:100%;}

@media (max-width:767px){
  /* A phone gets the app, not a window onto it: the panel runs edge to edge — no
     canvas margin, edge, radius or shadow around it, which on a phone read as a
     card floating in the browser — and the shell keeps its content clear of the
     notch and the home indicator itself (each app's `viewport-fit=cover` hands
     those insets over). The ground is the panel's, so the insets do not show as
     a cream rim. */
  .lumi-appshell{background:var(--shell-panel);padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);}
  /* Off-canvas: the sidebar has no surface on desktop, so the drawer paints the
     canvas itself to stand on top of the panel. Drawer and backdrop are placed
     on the shell (absolute — the shell is the screen), not on the viewport
     (fixed): Safari on iOS 26 tints its status bar and toolbar from fixed
     elements at the screen's edges, and after a fixed backdrop had gone it kept
     both bars grey, as if the overlay had stuck. */
  .lumi-appshell__sidebar{position:absolute;top:0;left:0;bottom:0;z-index:var(--layer-sticky);padding:env(safe-area-inset-top) 0 env(safe-area-inset-bottom) env(safe-area-inset-left);transform:translateX(-100%);transition:transform var(--dur-slow) var(--ease-out);background:var(--shell-canvas);touch-action:pan-y;overscroll-behavior:contain;}
  .lumi-appshell__sidebar--open{transform:translateX(0);box-shadow:var(--shadow-lg);}
  .lumi-appshell__fold{display:none;}
  .lumi-appshell__main{margin:0;border:none;border-radius:0;box-shadow:none;}
  /* The app bar: the drawer's button at the start, Lumi at the end, in front of
     the page and fading into it like LumiShell's head (spec §6). */
  .lumi-appshell__topbar{position:relative;z-index:var(--layer-raised);align-items:center;gap:var(--space-2);min-height:var(--topbar-h);padding:0 var(--space-3) 0 var(--space-1);background:var(--shell-panel);}
  .lumi-appshell__topbar::after{content:'';position:absolute;left:0;right:0;top:100%;height:var(--fade-h);background:linear-gradient(to bottom,var(--shell-panel),transparent);pointer-events:none;}
  .lumi-appshell__main:has(> .lumi-appshell__banner) .lumi-appshell__topbar::after{display:none;}
  .lumi-appshell__menu{display:flex;width:44px;height:44px;border-radius:var(--radius-md);}
  .lumi-appshell__lumi{display:inline-flex;}
  /* Pressed, a control answers the finger at once (the tap highlight is off). */
  .lumi-appshell__menu,.lumi-appshell__lumi{transition:transform var(--dur-fast) var(--ease-out),background-color var(--dur-fast);}
  .lumi-appshell__menu:active{background:var(--surface-wash);transform:scale(.94);}
  .lumi-appshell__lumi:active{transform:scale(.96);}
  .lumi-appshell__backdrop{display:block;position:absolute;inset:0;z-index:var(--layer-dropdown);background:var(--overlay);opacity:0;pointer-events:none;touch-action:none;transition:opacity var(--dur-slow) var(--ease-out);}
  .lumi-appshell__backdrop--open{opacity:1;pointer-events:auto;}
  /* The page scrolls on its own: a fling past either end no longer drags the
     whole document (and the browser's pull-to-refresh) along with it. */
  .lumi-appshell__content{overscroll-behavior-y:contain;}
  .lumi-appshell__content--padded{padding:var(--space-4);--lumi-content-pad:var(--space-4);}
}

/* Print: the shell prints as its content (#738) — no sidebar, top strip, topbar,
   banner or dock, and the panel flows over as many pages as it needs instead of
   scrolling inside a viewport-high box. */
@media print{
  .lumi-appshell{display:block;height:auto;overflow:visible;background:none;}
  .lumi-appshell__top,.lumi-appshell__sidebar,.lumi-appshell__topbar,.lumi-appshell__banner,.lumi-appshell__dock,.lumi-appshell__backdrop{display:none;}
  .lumi-appshell__row{display:block;}
  .lumi-appshell__main{display:block;overflow:visible;margin:0;border:0;border-radius:0;box-shadow:none;background:none;}
  .lumi-appshell__content{overflow:visible;}
}
</style>

<!-- The boot frame's rules (issue 533): the shell's silhouette while an app boots.
     They live with the shell because they must keep its geometry. -->
<style src="./bootFrame.css"></style>
